import React, { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { Alert, Button, Card, Flex, Input, message, Table, TablePaginationConfig, theme, Typography } from 'antd';
import type { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { SearchOutlined } from '@ant-design/icons';

import AdminTemplate from '../../../layout/AdminTemplate';
import { getDisplayRange } from '../../../utils/getDisplayRange';
import useFeatureFlag, { FeatureFlag } from '../../../hooks/useFeatureFlag';
import useQueryStringParameters from '../../../utils/useQueryStringParameters';
import useQueryStringPagination from '../../../utils/useQueryStringPagination';
import { useCharbeePlanId, usePrerenderEmail } from '../../../hooks/usePrerenderUser';
import { ExportStatus, useExportDomainsCsvMutation, useGetDomainStatisticsQuery } from '../api/domainManagerApiSlice';
import { useVerifyDomainMutation } from '../../integrationWizard/api/integrationWizardSlice';
import { CONTENT_WRITE_ROLES } from '../../auth/roleCapabilities';
import { useIsRoleIn } from '../../../components/ButtonWithRole';
import { PointInTime } from '../../../components/PointInTime';
import { CustomTooltip } from '../../../components/Tooltip';
import { useEvent } from '../../events/hooks/useEvent';
import ExportButton from '../../../components/ExportButton';
import ExportCSVModal from '../../SeoReport/components/ExportCSVModal';
import AddDomainModal from './AddDomainModal';
import IntegrationFailedModal from './IntegrationFailedModal';
import LinksCell from './CacheExpirationCell';
import VerifiedStateCell from './VerifiedStateCell';
import ActionsCell from './ActionsCell';
import SEOReportCell from './SEOReportCell';
import type Domain from '../Domain';
import { buildDomainListQueryParameters, DomainListQueryParameters } from './DomainListQueryParameters';
import { UpgradePlanProvider } from '~features/UpgradePlan';
import { longNumberFormatter } from '~library/numbers';
import { Link } from 'react-router-dom';

const { Title, Text } = Typography;

const META_TAGS = {
  title: 'Domain Manager List - Prerender.io',
};

const PAGE_SIZE = 100;

const ADD_DOMAIN_DISABLED_MESSAGE = 'Only an account owner, an admin or a team member can add a domain.';

const getResponseMessage = (
  status: ExportStatus,
  email?: string
): { title: string; content: React.ReactNode } => {
  switch (status) {
    case ExportStatus.Queued:
      return {
        title: 'Domains List export scheduled',
        content: email ? (
          <Typography.Paragraph>
            We will email a download link to <strong>{email}</strong> when your export is ready. You can also access it
            from the <Link to="/notifications">Notifications Center</Link>.
          </Typography.Paragraph>
        ) : (
          <Typography.Paragraph>
            We have scheduled your export. You will get a download link in your email once the export is ready.
          </Typography.Paragraph>
        )
      };
    case ExportStatus.NotQueue:
      return {
        title: 'Export CSV',
        content: (
          // eslint-disable-next-line max-len
          <Typography.Paragraph>
            Click start to export all your cached URLs into a CSV. Please note that exporting URLs happens in the
            background and you&apos;ll be notified by email once it&apos;s done.
          </Typography.Paragraph>
        ),
      };
    case ExportStatus.AlreadyExists:
      return {
        title: 'Export CSV',
        content: (
          <Typography.Paragraph>CSV export has been already requested. Please try again later.</Typography.Paragraph>
        )
      };

    default:
      return {
        title: 'Export CSV',
        content: <Typography.Paragraph>Something bad has happened. We&apos;re looking into it...</Typography.Paragraph>
      };
  }
};

function parseApiResponse(data: Domain[]): DomainListItem[] {
  return data.map((item) => {
    return {
      id: item.id,
      domain: item.domain,
      created_at: item.created_at,
      number_of_urls: longNumberFormatter.format(item.number_of_urls),
      verified: item.middleware_installed,
      deleted: !!item.deletion_requested_at,
    };
  });
}

type DomainListItem = Omit<Domain, 'middleware_installed' | 'number_of_urls' | 'deletion_requested_at'> & {
  verified: boolean;
  number_of_urls: string;
  deleted: boolean;
};

type DomainsExportCSVModalProps = {
  visible: boolean;
  onClose: () => void;
  queryStrings: DomainListQueryParameters;
  exportDomainManagerCsv: ReturnType<typeof useExportDomainsCsvMutation>[0];
  userEmail: string;
};

function DomainsExportCSVModal({
  visible,
  onClose,
  queryStrings,
  exportDomainManagerCsv,
  userEmail,
}: DomainsExportCSVModalProps) {
  const [exportTriggered, setExportTriggered] = useState(false);
  const [title, setTitle] = useState('Export CSV');
  const [modalText, setModalText] = useState<React.ReactNode>(
    <Typography.Paragraph>Export Domains list as CSV?</Typography.Paragraph>
  );

  return (
    <ExportCSVModal
      exportTriggered={exportTriggered}
      modalTitle={title}
      onClose={() => {
        onClose();
        setTitle('Export CSV');
        setModalText(<Typography.Paragraph>Export Domains list as CSV?</Typography.Paragraph>);
        setExportTriggered(false);
      }}
      onExport={() => {
        const exportParams = {
          sort: queryStrings.sort,
          sortDirection: queryStrings.sortDirection,
          q: queryStrings.domain,
        };

        exportDomainManagerCsv(exportParams)
          .unwrap()
          .then((result: { status: ExportStatus }) => {
            const msg = getResponseMessage(result.status, userEmail);
            setTitle(msg.title);
            setModalText(msg.content);
          })
          .catch(() => {
            setTitle('Export CSV');
            setModalText(<Typography.Paragraph>Failed to export CSV</Typography.Paragraph>);
          })
          .finally(() => {
            setExportTriggered(true);
          });
      }}
      showModal={visible}
      text={modalText}
    />
  );
}

const DomainsListPage = () => {
  const [messageApi, contextHolder] = message.useMessage();

  const [showExportModal, setShowExportModal] = useState(false);
  const [showAddDomainModal, setShowAddDomainModal] = useState(false);

  const { queryStrings, updateParameter, updateMultipleParameters } =
    useQueryStringParameters<DomainListQueryParameters>(buildDomainListQueryParameters);
  const { pagination, updatePagination } = useQueryStringPagination(PAGE_SIZE);

  const { data, error, isFetching } = useGetDomainStatisticsQuery(
    { ...pagination, ...queryStrings },
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const [exportDomainManagerCsv] = useExportDomainsCsvMutation();
  const [verifyIntegration, verifyIntegrationResult] = useVerifyDomainMutation();

  const canAddDomain = useIsRoleIn(CONTENT_WRITE_ROLES);
  const canUseDomainDetails = useFeatureFlag([FeatureFlag.DomainsDetails]);
  const isSeoReportPageAvailable = useFeatureFlag([FeatureFlag.SeoReport]);

  const { track } = useEvent();
  const { token } = theme.useToken();

  const totalDomains = data?.totalCount || 0;

  const userEmail = usePrerenderEmail();
  const chargebeePlanId = useCharbeePlanId();

  const domains: DomainListItem[] = React.useMemo(() => {
    if (data) {
      return parseApiResponse(data.data);
    }
    return [];
  }, [data]);

  useEffect(() => {
    if (error) {
      messageApi.error('Failed to fetch domain statistics');
    }
  }, [error]);

  const handleTableChange = (
    latestPagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<DomainListItem> | SorterResult<DomainListItem>[]
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    updateMultipleParameters({
      sort: singleSorter.field as DomainListQueryParameters['sort'],
      sortDirection: singleSorter.order === 'descend' ? 'DESC' : 'ASC',
    });

    if (latestPagination.current !== pagination.page || latestPagination.pageSize !== pagination.pageSize)
      updatePagination(latestPagination.current, latestPagination.pageSize);
  };

  const debouncedSetTableParams = useCallback(
    debounce((newInput: string) => {
      updateParameter('domain', newInput);
    }, 1500),
    []
  );

  const displayRange = getDisplayRange({ current: pagination.page, pageSize: pagination.pageSize }, totalDomains);

  const onVerifyIntegration = (url: string) => {
    verifyIntegration({ url: `https://${url}` })
      .unwrap()
      .then(({ validationResult }) => {
        track('Domain integration verified', {
          domain: url,
          subscription_plan: chargebeePlanId,
          prerenderIntegrationVerified: validationResult.working,
        });
        if (validationResult.working) {
          messageApi.success('Domain integration verified successfully');
        }
      })
      .catch(() => {
        track('Unexpected error - Domain integration verified', {
          domain: url,
          subscription_plan: chargebeePlanId,
        });

        messageApi.error('Failed to verify the domain integration, please try again later');
      });
  };

  const onOpenAddDomainModal = () => {
    track('Add domain clicked', { subscription_plan: chargebeePlanId });
    setShowAddDomainModal(true);
  };

  const onCloseVerifyIntegrationModal = () => {
    verifyIntegrationResult.reset();
  };

  const isLoading = isFetching || verifyIntegrationResult.isLoading;

  const renderVerifiedStateCell = React.useCallback(
    (record: DomainListItem) => <VerifiedStateCell verified={record.verified} deleted={record.deleted} />,
    []
  );

  const renderLinksCell = React.useCallback(
    (record: DomainListItem) => <LinksCell domain={record.domain} disabled={record.deleted} />,
    []
  );

  const renderSeoReportCell = React.useCallback(
    (record: DomainListItem) => <SEOReportCell id={record.id} domain={record.domain} deleted={record.deleted} />,

    []
  );

  const renderActionsCell = React.useCallback((record: DomainListItem) => {
    return (
      <ActionsCell
        id={record.id}
        domain={record.domain}
        numberOfUrls={record.number_of_urls}
        verified={record.verified}
        onVerifyIntegration={() => onVerifyIntegration(record.domain)}
        disabled={record.deleted}
      />
    );
  }, []);

  const sortDirection = queryStrings.sortDirection === 'ASC' ? 'ascend' : 'descend';

  return (
    <AdminTemplate metaTags={META_TAGS}>
      <UpgradePlanProvider>
        {contextHolder}
        {canUseDomainDetails && (
          <DomainsExportCSVModal
            visible={showExportModal}
            onClose={() => setShowExportModal(false)}
            queryStrings={queryStrings}
            exportDomainManagerCsv={exportDomainManagerCsv}
            userEmail={userEmail}
          />
        )}

        <AddDomainModal open={showAddDomainModal} onClose={() => setShowAddDomainModal(false)} />
        <IntegrationFailedModal
          open={verifyIntegrationResult.isSuccess && !verifyIntegrationResult.data?.validationResult.working}
          onClose={onCloseVerifyIntegrationModal}
          errors={verifyIntegrationResult.data?.validationResult.errors}
        />

        <Flex gap={16} vertical={true} style={{ marginBottom: '16px' }}>
          <div>
            <Title level={2}>Domain Manager</Title>
            <Typography.Paragraph type="secondary">
              Monitor your activity and adjust settings for each cached domain.
            </Typography.Paragraph>
          </div>

          <Alert message="Please note the list refreshes every 24 hours" type="info" showIcon />
          <Card
            title={
              <Flex align="center" justify="space-between">
                <Typography.Title level={5}>My Domains</Typography.Title>
                {canAddDomain ? (
                  <Button type="primary" onClick={onOpenAddDomainModal}>
                    Add Domain
                  </Button>
                ) : (
                  // The inner `pointer-events: none` wrapper is what makes the tooltip reachable:
                  // browsers suppress mouse events over a disabled button, so the hover has to land
                  // on the tooltip's own span. Same shape as RerunPlanGate.
                  <CustomTooltip
                    placement="top"
                    message={ADD_DOMAIN_DISABLED_MESSAGE}
                    wrapperStyle={{ cursor: 'not-allowed' }}
                  >
                    <span style={{ pointerEvents: 'none' }}>
                      <Button type="primary" disabled>
                        Add Domain
                      </Button>
                    </span>
                  </CustomTooltip>
                )}
              </Flex>
            }
          >
            <Flex justify="space-between">
              <Input
                placeholder="search domain"
                allowClear
                defaultValue={queryStrings.domain}
                onChange={(e) => debouncedSetTableParams(e.target.value)}
                prefix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
                style={{ marginBottom: 16, width: '30%' }}
              />
              <div>
                <Text>
                  Displaying {displayRange} domains from {Intl.NumberFormat().format(totalDomains)}
                </Text>
                {canUseDomainDetails && (
                  <ExportButton onClick={() => setShowExportModal(true)} />
                )}
              </div>
            </Flex>
            <Table
              loading={isLoading}
              pagination={{
                current: pagination.page,
                pageSize: pagination.pageSize,
                total: totalDomains,
              }}
              dataSource={domains}
              onChange={handleTableChange}
              bordered
              rowKey="domain"
              onRow={(record) => ({
                style: record.deleted ? { color: token.colorTextSecondary, textDecoration: 'line-through' } : {},
              })}
            >
              <Table.Column title="Domain" dataIndex="domain" key="domain" width="60%" />
              <Table.Column
                title="Created At"
                dataIndex="created_at"
                key="created_at"
                sorter
                defaultSortOrder={queryStrings.sort === 'created_at' ? sortDirection : undefined}
                className="col-min"
                render={(createdAt: DomainListItem['created_at']) => <PointInTime value={createdAt} />}
              />
              <Table.Column
                title="URLs"
                dataIndex="number_of_urls"
                key="number_of_urls"
                sorter
                defaultSortOrder={queryStrings.sort === 'number_of_urls' ? sortDirection : undefined}
                align="left"
              />
              <Table.Column
                title="Integration"
                dataIndex="verified"
                key="verified"
                render={(_value, record: DomainListItem) => renderVerifiedStateCell(record)}
              />
              {isSeoReportPageAvailable && (
                <Table.Column
                  title="SEO Report"
                  align="left"
                  render={(_value, record: DomainListItem) => renderSeoReportCell(record)}
                />
              )}
              <Table.Column
                title="Cache Expiration Rules"
                render={(_value, record: DomainListItem) => renderLinksCell(record)}
              />
              <Table.Column
                title="Actions"
                width="5%"
                render={(_value, record: DomainListItem) => renderActionsCell(record)}
              />
            </Table>
          </Card>
        </Flex>
      </UpgradePlanProvider>
    </AdminTemplate>
  );
};

export default DomainsListPage;
